import { SuccessToast } from '@strata/react-toast-message';
import { isTaskTodo, Worklist, WorklistTask } from '@strata/react-worklist';
import { ComponentProps, useCallback } from 'react';
import { Location, NavigationType, useLocation, useNavigate } from 'react-router-dom';

import { AppRoutes } from 'services/routes';
import { useAppDispatch } from 'store';
import { usePostUnableToCompleteTaskMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

/** Show dialog if coming from worklist page */
export const showDialogForWorklistTypes = ({
    types,
    navigationType,
    location,
}: {
    types: (typeof Worklist.WorkTaskType)[keyof typeof Worklist.WorkTaskType][];
    navigationType: NavigationType;
    location: Location<{ worklistTask?: WorklistTask }>;
}) =>
    !!location.state?.worklistTask?.task_type &&
    types.includes(location.state.worklistTask.task_type) &&
    // Ensure dialog is only shown once (initial overview tab & not back navigation)
    navigationType !== NavigationType.Pop;

const backToWorklist = {
    backToDestination: {
        route: AppRoutes.worklist,
        text: 'Worklist',
    },
};

export const useOnTaskActionClick = (fromWorklistPage = false) => {
    const navigate = useNavigate();
    const location = useLocation() as Location<{
        worklistTask?: WorklistTask;
        fromWorklistPage?: boolean;
    }>;
    return useCallback(
        (worklistTask: WorklistTask) => {
            if (
                [
                    Worklist.WorkTaskType.PENDINTAKE,
                    Worklist.WorkTaskType.ACCEPTANCE,
                    Worklist.WorkTaskType.REBILL,
                    Worklist.WorkTaskType.MANSUBTASK,
                    Worklist.WorkTaskType.NBD,
                ].includes(worklistTask.task_type)
            ) {
                if (!isTaskTodo(worklistTask)) {
                    navigate(AppRoutes.wonReferralDetails(worklistTask.referral_id), {
                        state: { ...backToWorklist },
                    });
                } else {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(worklistTask.referral_id),
                            hash:
                                worklistTask.task_type === Worklist.WorkTaskType.MANSUBTASK
                                    ? 'performance'
                                    : location.hash,
                        },
                        {
                            state: {
                                ...backToWorklist,
                                ...location.state,
                                worklistTask,
                                fromWorklistPage,
                            },
                        },
                    );
                }
            }
        },
        [fromWorklistPage, location, navigate],
    );
};

export const useOnUnableToComplete = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [postUnableToCompleteTask] = usePostUnableToCompleteTaskMutation();
    return useCallback<Required<ComponentProps<typeof Worklist>>['onUnableToComplete']>(
        ({ task, reason, beginSupportConversation }) => {
            (async () => {
                await postUnableToCompleteTask({ taskId: task.task_id, reason }).unwrap();
                await dispatch(fetchWorklistTasks());
                SuccessToast({ message: 'Task marked as unable to complete.' });
                if (beginSupportConversation) {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(task.referral_id),
                            hash: 'messages',
                        },
                        { state: { worklistTask: task, beginSupportConversation } },
                    );
                }
            })();
        },
        [postUnableToCompleteTask, dispatch, navigate],
    );
};

export const useReturnToWorklistCheck = () => {
    const navigate = useNavigate();
    const location = useLocation() as Location<{
        worklistTask?: WorklistTask;
        fromWorklistPage?: boolean;
    }>;

    return useCallback(() => {
        // If completing from worklist task, navigate back afterwards
        if (location.state?.worklistTask) {
            navigate(
                // Navigate to either worklist page or referral worklist tab
                location.state.fromWorklistPage
                    ? AppRoutes.worklist
                    : { ...location, hash: 'worklist' },
            );
        }
    }, [navigate, location]);
};
